import { useCallback, useMemo, useRef, useState } from 'react'
import { useBoolean } from 'react-use'

type PopoverTransition = 'close' | 'open'

export interface UsePopoverTransitionRequest {
  to: PopoverTransition
}

export interface UsePopoverStateOptions {
  validateTransitionRequest?: (
    request: UsePopoverTransitionRequest
  ) => boolean | Promise<boolean>
  initialState?: boolean
  onTransitionValidationFailed?: (request: UsePopoverTransitionRequest) => void
}

/**
 * Tracks open/closed UI state and returns helpers to control it.
 *
 * `open()` and `close()` run through one optional transition validation.
 * If allowed, state updates to the requested transition target.
 * If denied, state remains unchanged.
 *
 * `toggle()` tracks pending intent between renders so rapid async toggles
 * alternate predictably instead of reusing stale state snapshots.
 *
 * This is intentionally generic so it can be reused by drawers, popovers, menus,
 * and dialog-like UI without assuming component-specific semantics.
 */
export function usePopoverState(
  initialStateOrOptions: boolean | UsePopoverStateOptions = false
) {
  // Support both boolean and options signature by converting them into a shared object shape.
  const {
    initialState = false,
    validateTransitionRequest = undefined,
    onTransitionValidationFailed = undefined
  } = typeof initialStateOrOptions === 'boolean'
    ? { initialState: initialStateOrOptions }
    : initialStateOrOptions

  const [isOpen, setIsOpen] = useBoolean(initialState)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Tracks the newest transition request so stale async results are ignored.
  const latestTransitionIdRef = useRef(0)
  // Stores the last successfully applied visible state.
  const lastAppliedVisibilityStateRef = useRef(initialState)
  // Tracks latest requested visibility between renders so rapid toggles alternate correctly.
  const pendingVisibilityIntentRef = useRef(initialState)

  // Commits the final visibility state to both React state and internal refs.
  const applyVisibilityState = useCallback(
    (nextIsOpen: boolean) => {
      lastAppliedVisibilityStateRef.current = nextIsOpen
      pendingVisibilityIntentRef.current = nextIsOpen
      setIsOpen(nextIsOpen)
    },
    [setIsOpen]
  )

  // Restores pending intent after failed validation and returns the original validation result.
  const finalizeTransitionValidationResult = useCallback(
    (wasTransitionValidationSuccessful: boolean) => {
      if (!wasTransitionValidationSuccessful) {
        pendingVisibilityIntentRef.current =
          lastAppliedVisibilityStateRef.current
      }

      return wasTransitionValidationSuccessful
    },
    []
  )

  // Initiates a visibility change request and supports both sync and async validation.
  const initiateTransition = useCallback(
    async (to: PopoverTransition) => {
      // Marks this as the newest transition attempt so older async results cannot overwrite it.
      const transitionId = ++latestTransitionIdRef.current
      setIsTransitioning(true)

      try {
        if (!validateTransitionRequest) return false

        let isTransitionValid = false

        try {
          isTransitionValid = await validateTransitionRequest({ to })
        } catch {
          /*
           * We swallow this error here with the assumption the validator will properly handle
           * any exceptions it may encounter
           */
          isTransitionValid = false
        }

        // Drops stale async completions if a newer transition started while awaiting validation.
        if (transitionId !== latestTransitionIdRef.current) return false

        if (!isTransitionValid) {
          onTransitionValidationFailed?.({ to })
          return false
        }

        const nextIsOpen = to === 'open'

        applyVisibilityState(nextIsOpen)
        return true
      } finally {
        if (transitionId === latestTransitionIdRef.current) {
          setIsTransitioning(false)
        }
      }
    },
    [
      applyVisibilityState,
      validateTransitionRequest,
      onTransitionValidationFailed
    ]
  )

  const close = useCallback(async () => {
    // Records close intent before running validation so follow-up requests evaluate against latest intent.
    pendingVisibilityIntentRef.current = false

    // Immediately update state if no transition validator exists and return successfully closed.
    if (!validateTransitionRequest) {
      applyVisibilityState(false)
      return true
    }

    const wasTransitionValidationSuccessful = await initiateTransition('close')

    return finalizeTransitionValidationResult(wasTransitionValidationSuccessful)
  }, [
    applyVisibilityState,
    finalizeTransitionValidationResult,
    initiateTransition,
    validateTransitionRequest
  ])

  const open = useCallback(async () => {
    // Records open intent before running validation so follow-up requests evaluate against latest intent.
    pendingVisibilityIntentRef.current = true

    // Immediately update state if no transition validator exists and return successfully opened.
    if (!validateTransitionRequest) {
      applyVisibilityState(true)
      return true
    }

    const wasTransitionValidationSuccessful = await initiateTransition('open')

    return finalizeTransitionValidationResult(wasTransitionValidationSuccessful)
  }, [
    applyVisibilityState,
    finalizeTransitionValidationResult,
    initiateTransition,
    validateTransitionRequest
  ])

  const toggle = useCallback(async () => {
    /*
     * Preserve deterministic toggle behavior when no validation exists.
     * Update intent synchronously so rapid successive toggles still
     * alternate to the expected open/closed state.
     */
    if (!validateTransitionRequest) {
      const nextIsOpen = !pendingVisibilityIntentRef.current
      applyVisibilityState(nextIsOpen)
      return true
    }

    // Unlike open/close, toggle computes and stores the next intent first so back-to-back async toggles alternate correctly.
    const nextIsOpen = !pendingVisibilityIntentRef.current
    pendingVisibilityIntentRef.current = nextIsOpen

    const wasToggleSuccessful = await initiateTransition(
      nextIsOpen ? 'open' : 'close'
    )

    return finalizeTransitionValidationResult(wasToggleSuccessful)
  }, [
    applyVisibilityState,
    finalizeTransitionValidationResult,
    initiateTransition,
    validateTransitionRequest
  ])

  return useMemo(
    () => ({
      isOpen,
      isTransitioning,
      close,
      open,
      toggle
    }),
    [close, isOpen, isTransitioning, open, toggle]
  )
}
